import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  DateRangeField,
  DockedCard,
  Field,
  Icon,
  Input,
  Note,
  type DateRangeValue,
} from '@repo/design-system';
import type { AuthoredOpeningHoursVersion } from '@repo/frontend-types';
import { semantic, space, typeRole } from '@repo/design-tokens';

import DeleteSpecial from './DeleteSpecial';
import {
  calendarKey,
  runningDatesProblem,
  specialClash,
  specialDateLimits,
  specialEnd,
  specialName,
  specialNameProblem,
  SPECIAL_NAME_TOO_LONG,
  type CalendarGroup,
} from './hoursModel';
import {
  actionProblem,
  type HoursActionResult,
  type SpecialPeriodFields,
} from './useOpeningHoursEditor';

/**
 * **The special period card** (spec twin-details-page-hours-special, R6; frame 07): the name and the
 * dates of a special period, docked beside its week at 360 px. Not modal: the list and the week stay
 * usable.
 *
 * Nothing is pressed to save, as everywhere on this step. A new period is added the moment it has a
 * valid name, both dates and no clash with another special period; a saved one's name or dates are
 * changed on each commit (blur or Enter for the name, a committed date for the dates). The browser
 * checks first — a clash, a running period's rule, a name the store would refuse — and then nothing
 * is sent and the field goes back. The store stays the authority: its sentence is shown when the two
 * disagree.
 *
 * The step owns which period is open, the selection and the announcements; this card owns its draft,
 * which a later read never overwrites (spec Failure States: Overtaken).
 */

export const SPECIAL_CHANGED =
  'The hours changed while you were editing this period. Check it and try again.';
export const SPECIAL_UNKNOWN = 'Couldn’t tell whether the special period was saved.';
export const SPECIAL_INFO =
  'Starts as a copy of standard hours. Days you change show in lavender on the week.';
export const SPECIAL_NO_OVERLAP = 'No overlap with other special periods';
export const SPECIAL_CLASH_RULE = 'Two special periods can’t share a date.';

export interface SpecialPeriodCardProps {
  /** The saved period, or null while a new one is being made. */
  calendar: AuthoredOpeningHoursVersion | null;
  /** Its group today; null for a new period. */
  group: CalendarGroup | null;
  versions: readonly AuthoredOpeningHoursVersion[];
  /** The twin's date. */
  today: string;
  onAdd: (fields: SpecialPeriodFields) => Promise<HoursActionResult>;
  /** Added: the step selects it under its group and says so. */
  onAdded: (fields: SpecialPeriodFields) => void;
  onChange: (
    calendar: AuthoredOpeningHoursVersion,
    fields: SpecialPeriodFields,
  ) => Promise<HoursActionResult>;
  /** Renamed or re-dated: the step follows it to its new key and says what changed. */
  onChanged: (before: AuthoredOpeningHoursVersion, fields: SpecialPeriodFields) => void;
  canDelete: boolean;
  onDelete: () => Promise<HoursActionResult>;
  onDeleted: () => void;
  /** An add or change is in flight, or has settled. */
  onSending: (sending: boolean) => void;
  /** The draft as typed, so the step can show its week and know whether anything was typed. */
  onDraft: (draft: { name: string; from: string | null; to: string | null }) => void;
  /** Say, in the step's polite region, a refusal or a problem (R9). */
  onSay: (sentence: string) => void;
  /** A problem that arrived after the delete popover closed (R7). */
  onLateProblem: (sentence: string) => void;
  /** *Done*, the close button or Escape. */
  onClose: () => void;
  /** Where focus goes when the card goes with focus in it: the step points it before closing. */
  returnFocusTo: { readonly current: HTMLElement | null };
  /** Move focus to *Name* on open (from *Add Special Period*, R8.2). */
  focusName: boolean;
  className?: string;
}

const fieldsOf = (v: AuthoredOpeningHoursVersion): SpecialPeriodFields => ({
  name: specialName(v),
  from: v.effectiveDateFrom,
  to: specialEnd(v),
});

const SpecialPeriodCard: React.FC<SpecialPeriodCardProps> = ({
  calendar,
  group,
  versions,
  today,
  onAdd,
  onAdded,
  onChange,
  onChanged,
  canDelete,
  onDelete,
  onDeleted,
  onSending,
  onDraft,
  onSay,
  onLateProblem,
  onClose,
  returnFocusTo,
  focusName,
  className,
}) => {
  const stored = calendar ? fieldsOf(calendar) : null;
  /** The name as typed, and as last committed (Escape goes back to this). */
  const [name, setName] = useState(stored?.name ?? '');
  const [committedName, setCommittedName] = useState(stored?.name ?? '');
  const [range, setRange] = useState<DateRangeValue>({
    from: stored?.from ?? null,
    to: stored?.to ?? null,
  });
  const [sending, setSendingState] = useState(false);
  /** Read by a commit from the same tick: the blur a disabled field fires comes before a render. */
  const sendingRef = useRef(false);
  const setSending = (next: boolean) => {
    sendingRef.current = next;
    setSendingState(next);
    onSending(next);
  };
  const [nameProblem, setNameProblem] = useState<string | null>(null);
  /** A refusal or problem from the browser's checks or the store (R6.4), in the status region. */
  const [problem, setProblem] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const clashId = useId();
  const statusId = useId();

  useEffect(() => {
    if (focusName) nameRef.current?.focus();
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const draftRef = useRef(onDraft);
  draftRef.current = onDraft;
  useEffect(() => {
    draftRef.current({ name, from: range.from, to: range.to });
  }, [name, range.from, range.to]);

  const ownKey = calendar ? calendarKey(calendar) : null;
  const clash =
    range.from && range.to
      ? specialClash(versions, { from: range.from, to: range.to }, ownKey)
      : null;
  const limits = specialDateLimits(calendar, group, today);

  const say = (sentence: string) => {
    setProblem(sentence);
    onSay(sentence);
  };

  /*
   * Put the fields back to what is stored: a saved period's commit that did not land. After the next
   * render, because a refusal re-reads before it answers and the period stored NOW is the one that
   * render carries (spec Failure States: the field goes back to the stored value).
   */
  const [reverts, setReverts] = useState(0);
  const revert = () => setReverts(n => n + 1);
  const calendarRef = useRef(calendar);
  calendarRef.current = calendar;
  useEffect(() => {
    const now = calendarRef.current;
    if (reverts === 0 || !now) return;
    const back = fieldsOf(now);
    setName(back.name);
    setCommittedName(back.name);
    setRange({ from: back.from, to: back.to });
  }, [reverts]);

  /*
   * Disabling the focused field drops focus: hand it back to *Name* once the answer is in (R6.7).
   * After the render that re-enables it, because a disabled field takes no focus.
   */
  const refocusRef = useRef(false);
  const keepFocus = () => {
    refocusRef.current = true;
  };
  useEffect(() => {
    if (sending || !refocusRef.current) return;
    refocusRef.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) nameRef.current?.focus();
  }, [sending]);

  // ── A new period: added once it is complete and clear (R6.3). ──
  const tryAdd = async (nextName: string, next: DateRangeValue) => {
    if (sendingRef.current || calendar) return;
    if (specialNameProblem(nextName) !== null || !next.from || !next.to) return;
    if (specialClash(versions, { from: next.from, to: next.to }, null) !== null) return;
    const fields = { name: nextName.trim(), from: next.from, to: next.to };
    setSending(true);
    setProblem(null);
    const result = await onAdd(fields);
    setSending(false);
    const sentence = actionProblem(result, SPECIAL_CHANGED, SPECIAL_UNKNOWN);
    if (sentence === null) {
      setName(fields.name);
      setCommittedName(fields.name);
      setProblem(null);
      onAdded(fields);
    } else {
      // The period stays unsaved, with its fields as typed.
      say(sentence);
    }
    keepFocus();
  };

  // ── A saved period: one change per commit, the browser's checks first (R6.3). ──
  const tryChange = async (fields: SpecialPeriodFields) => {
    // One change at a time: a commit while one is in flight waits for its answer (spec Failure States).
    if (sendingRef.current || !calendar || !stored) return;
    if (fields.name === stored.name && fields.from === stored.from && fields.to === stored.to) {
      return;
    }
    const refusal =
      specialClash(versions, { from: fields.from, to: fields.to }, ownKey) ??
      runningDatesProblem(calendar, fields, today);
    if (refusal !== null) {
      say(refusal.startsWith('Overlaps ') ? `${refusal}. ${SPECIAL_CLASH_RULE}` : refusal);
      revert();
      return;
    }
    setSending(true);
    setProblem(null);
    const result = await onChange(calendar, fields);
    setSending(false);
    const sentence = actionProblem(result, SPECIAL_CHANGED, SPECIAL_UNKNOWN);
    if (sentence === null) {
      setName(fields.name);
      setCommittedName(fields.name);
      setRange({ from: fields.from, to: fields.to });
      setProblem(null);
      onChanged(calendar, fields);
    } else {
      say(sentence);
      revert();
    }
    keepFocus();
  };

  const commitName = () => {
    const nameIssue = specialNameProblem(name);
    if (calendar && stored) {
      if (name === committedName) return;
      if (nameIssue !== null) {
        // A saved period keeps its name.
        setNameProblem(nameIssue);
        onSay(nameIssue);
        setName(committedName);
        return;
      }
      setNameProblem(null);
      void tryChange({ name: name.trim(), from: stored.from, to: stored.to });
      return;
    }
    // A blank name only waits; one too long is said. Every commit retries the add: one refused
    // while a save was in flight is added when the person commits again (spec Failure States).
    setNameProblem(nameIssue === SPECIAL_NAME_TOO_LONG ? nameIssue : null);
    if (nameIssue === SPECIAL_NAME_TOO_LONG && name !== committedName) onSay(nameIssue);
    setCommittedName(name);
    void tryAdd(name, range);
  };

  const commitRange = (next: DateRangeValue) => {
    setRange(next);
    if (calendar && stored) {
      // `DateRangeField` never commits a saved period without both ends after a pick of the first;
      // a half range is the person mid-pick.
      if (next.from && next.to) {
        void tryChange({ name: stored.name, from: next.from, to: next.to });
      }
      return;
    }
    const nextClash =
      next.from && next.to ? specialClash(versions, { from: next.from, to: next.to }, null) : null;
    if (nextClash) onSay(`${nextClash}. ${SPECIAL_CLASH_RULE}`);
    void tryAdd(committedName, next);
  };

  const footer = (
    <div className="card-foot">
      {calendar && canDelete && (
        <DeleteSpecial
          calendar={calendar}
          onDelete={onDelete}
          onDeleted={onDeleted}
          onLateProblem={onLateProblem}
        />
      )}
      <Button
        className="done"
        variant="primary"
        size="sm"
        iconLeft={<Icon name="check" />}
        onClick={onClose}
      >
        Done
      </Button>
    </div>
  );

  return (
    <DockedCard
      className={className}
      placement="narrow"
      tone="lavender"
      icon={<Icon name="calendar-days" />}
      title="Special period"
      sub="Replaces standard hours on these dates"
      headingLevel={3}
      onClose={onClose}
      closeLabel="Close special period"
      returnFocusTo={returnFocusTo}
      footer={footer}
    >
      <div className="body">
        <Field label="Name" error={nameProblem ?? undefined}>
          {w => (
            <Input
              {...w}
              ref={nameRef}
              onGlass
              value={name}
              disabled={sending}
              onChange={e => setName(e.target.value)}
              onBlur={commitName}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitName();
                } else if (e.key === 'Escape' && name !== committedName) {
                  // The name's own Escape: revert, and keep the card open (R8.3).
                  e.preventDefault();
                  setName(committedName);
                }
              }}
            />
          )}
        </Field>

        <DateRangeField
          label="Dates"
          value={range}
          onChange={commitRange}
          min={limits.min}
          max={limits.max}
          today={today}
          disabled={sending}
          invalid={clash !== null}
          aria-describedby={range.from && range.to ? clashId : undefined}
        />

        <Note tone="peri" icon={<Icon name="circle-info" size="xs" />}>
          {SPECIAL_INFO}
        </Note>

        {range.from && range.to && (
          <Note
            id={clashId}
            tone={clash ? 'error' : 'mint'}
            icon={<Icon name={clash ? 'circle-exclamation' : 'check'} size="xs" />}
          >
            {clash ? `${clash}. ${SPECIAL_CLASH_RULE}` : SPECIAL_NO_OVERLAP}
          </Note>
        )}

        <div role="status" id={statusId} className="status">
          {sending ? (
            <p className="saving">Saving…</p>
          ) : (
            problem && (
              <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />}>
                {problem}
              </Note>
            )
          )}
        </div>
      </div>
    </DockedCard>
  );
};

export default styled(SpecialPeriodCard)`
  .body {
    display: flex;
    flex-direction: column;
    gap: ${space[4]};
  }

  /* Empty, it stays in the accessibility tree (a live region is heard only once registered) but takes no gap. */
  .status:empty {
    position: absolute;
  }

  .saving {
    margin: 0;
    ${typeRole.caption1};
    color: ${semantic.text.secondary};
  }

  .card-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${space[2]};
    width: 100%;
  }

  .card-foot .done {
    margin-left: auto;
  }
`;
